import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Autocomplete,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  loadGroupDropdown,
  createNewAccount,
  editGroupOrAccount,
  initiateTwitterOAuth,
} from "../../Action/api";
// import { getTodayDate } from "../../Utils/dateHelpers"; // ADDED: Import date helper
import { getTodayDate } from "../../Utils/dateHelpers"; // ADDED: Import date helper
import { PROVIDER_GUIDES } from "./providerGuides";

// ── Social Platform Definitions ──────────────────────────────────────────────
const PLATFORMS = [
  // ── Active platforms ────────────────────────────────────────────────────
  {
    name: "Facebook",
    providerName: "Facebook",
    color: "#1877F2",
    active: true,
    connectOptions: ["Connect Page", "Connect Group"],
    svg: (
      <svg width="52" height="52" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
        <circle cx="26" cy="26" r="26" fill="#1877F2"/>
        <path d="M28.5 27h3.5l.6-4.5H28.5V20c0-1.3.6-2.5 2.5-2.5H32.5V13.4S31 13 29.5 13C25.5 13 23 15.8 23 20v2.5h-3.5V27H23v11h5.5V27z" fill="white"/>
      </svg>
    ),
  },
  {
    name: "LinkedIn",
    providerName: "LinkedIn",
    color: "#0A66C2",
    active: true,
    connectOptions: ["Connect Profile", "Connect Page"],
    svg: (
      <svg width="52" height="52" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
        <circle cx="26" cy="26" r="26" fill="#0A66C2"/>
        <rect x="14" y="22" width="5" height="17" fill="white" rx="1"/>
        <circle cx="16.5" cy="17" r="3" fill="white"/>
        <path d="M25 22h5v2.5c.8-1.2 2.5-3 5.5-3C40 21.5 41 25 41 28.5V39h-5v-9.5c0-2.5-.5-4.5-3-4.5s-3.5 2-3.5 4.5V39h-5V22z" fill="white"/>
      </svg>
    ),
  },
  {
    name: "Instagram",
    providerName: "Instagram",
    color: "#E1306C",
    active: true,
    connectOptions: ["Connect Personal", "Connect Business or Creator"],
    svg: (
      <svg width="52" height="52" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="igGrad" cx="30%" cy="107%" r="150%">
            <stop offset="0%" stopColor="#fdf497"/>
            <stop offset="5%" stopColor="#fdf497"/>
            <stop offset="45%" stopColor="#fd5949"/>
            <stop offset="60%" stopColor="#d6249f"/>
            <stop offset="90%" stopColor="#285AEB"/>
          </radialGradient>
        </defs>
        <circle cx="26" cy="26" r="26" fill="url(#igGrad)"/>
        <rect x="16" y="16" width="20" height="20" rx="5" fill="none" stroke="white" strokeWidth="2.5"/>
        <circle cx="26" cy="26" r="5.5" fill="none" stroke="white" strokeWidth="2.5"/>
        <circle cx="33" cy="19" r="1.5" fill="white"/>
      </svg>
    ),
  },
  {
    name: "Twitter",
    providerName: "Twitter",
    color: "#000000",
    active: true,
    oauth: true, // Connects via OAuth redirect (twitter-oauth API) instead of the manual token form
    connectOptions: ["Connect via X"],
    svg: (
      <svg width="52" height="52" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
        <circle cx="26" cy="26" r="26" fill="#000000"/>
        <path d="M13 14l10.5 13.5L13 38h3l9-10 7 10H38L27 23.5 36.5 14h-3L23 23l-6.5-9H13z" fill="white"/>
      </svg>
    ),
  },
  // ── Coming Soon platforms ───────────────────────────────────────────────
  {
    name: "Pinterest",
    providerName: "Pinterest",
    color: "#E60023",
    active: false,
    connectOptions: ["Connect Board"],
    svg: (
      <svg width="52" height="52" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
        <circle cx="26" cy="26" r="26" fill="#E60023"/>
        <path d="M26 13C18.3 13 12 19.3 12 27c0 5.8 3.5 10.8 8.6 12.9-.1-1.1-.1-2.7.2-3.8l1.8-7.5s-.5-.9-.5-2.3c0-2.1 1.3-3.7 2.8-3.7 1.3 0 2 1 2 2.1 0 1.3-.8 3.3-1.3 5.1-.4 1.5.8 2.7 2.2 2.7 2.6 0 4.4-2.8 4.4-6.7 0-3.5-2.5-6-6.1-6-4.1 0-6.5 3.1-6.5 6.3 0 1.2.5 2.5 1.1 3.3.1.1.1.3.1.4l-.4 1.7c-.1.3-.3.4-.5.2-1.7-.8-2.7-3.3-2.7-5.3 0-4.3 3.1-8.3 9-8.3 4.7 0 8.4 3.3 8.4 7.8 0 4.7-2.9 8.4-7 8.4-1.4 0-2.6-.7-3-1.5l-.8 3c-.3 1.2-1.1 2.6-1.6 3.5.7.2 1.5.3 2.3.3 7.7 0 14-6.3 14-14S33.7 13 26 13z" fill="white"/>
      </svg>
    ),
  },
  {
    name: "Google Business",
    providerName: "Google Business",
    color: "#4285F4",
    active: false,
    connectOptions: ["Connect Profile"],
    svg: (
      <svg width="52" height="52" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
        <circle cx="26" cy="26" r="26" fill="#ffffff" stroke="#e2e8f0" strokeWidth="1.5"/>
        <path d="M38.5 26.3c0-.9-.1-1.7-.2-2.5H26v4.7h7c-.3 1.6-1.3 3-2.7 3.9v3.3h4.4c2.6-2.4 4.1-5.9 4.1-9.4z" fill="#4285F4"/>
        <path d="M26 39c3.5 0 6.5-1.2 8.7-3.2l-4.4-3.3c-1.2.8-2.6 1.2-4.3 1.2-3.3 0-6.1-2.2-7.1-5.3h-4.5v3.4C16.6 36.4 21 39 26 39z" fill="#34A853"/>
        <path d="M18.9 28.4c-.3-.8-.4-1.6-.4-2.4s.1-1.6.4-2.4v-3.4h-4.5C13.3 22.1 13 24 13 26s.3 3.9 1.4 5.8l4.5-3.4z" fill="#FBBC05"/>
        <path d="M26 18.3c1.9 0 3.5.6 4.8 1.8l3.6-3.6C32.5 14.5 29.5 13 26 13c-5 0-9.4 2.6-11.6 6.6l4.5 3.4c1-3.1 3.8-5.3 7.1-5.3z" fill="#EA4335"/>
      </svg>
    ),
  },
  {
    name: "YouTube",
    providerName: "YouTube",
    color: "#FF0000",
    active: false,
    connectOptions: ["Connect Channel"],
    svg: (
      <svg width="52" height="52" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
        <circle cx="26" cy="26" r="26" fill="#FF0000"/>
        <path d="M39 20.6c-.4-1.5-1.6-2.7-3.2-3.1C33 17 26 17 26 17s-7 0-9.8.5c-1.5.4-2.7 1.6-3.2 3.1C12.5 23.5 12.5 26 12.5 26s0 2.5.5 5.4c.4 1.5 1.6 2.7 3.2 3.1C19 35 26 35 26 35s7 0 9.8-.5c1.5-.4 2.7-1.6 3.2-3.1C39.5 28.5 39.5 26 39.5 26s0-2.5-.5-5.4zM23 29.8V22.2l6.5 3.8-6.5 3.8z" fill="white"/>
      </svg>
    ),
  },
  {
    name: "TikTok",
    providerName: "TikTok",
    color: "#010101",
    active: false,
    connectOptions: ["Connect Business"],
    svg: (
      <svg width="52" height="52" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
        <circle cx="26" cy="26" r="26" fill="#010101"/>
        <path d="M35.5 20.1c-2-.4-3.6-1.7-4.4-3.4v12.2c0 3.8-3.1 6.9-6.9 6.9s-6.9-3.1-6.9-6.9 3.1-6.9 6.9-6.9c.5 0 .9.1 1.4.2v4.4c-.4-.1-.9-.2-1.4-.2-1.6 0-2.9 1.3-2.9 2.9s1.3 2.9 2.9 2.9 2.9-1.3 2.9-2.9V14.5h4.2c.4 3.2 2.9 5.7 6.2 6.1v3.8c-.7 0-1.4-.1-2-.3v-4z" fill="white"/>
      </svg>
    ),
  },
  {
    name: "Tumblr",
    providerName: "Tumblr",
    color: "#35465C",
    active: false,
    connectOptions: ["Connect Blog"],
    svg: (
      <svg width="52" height="52" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
        <circle cx="26" cy="26" r="26" fill="#35465C"/>
        <path d="M28.5 14v4.5H32v4.5h-3.5V30c0 1.5.3 2 2 2H32v4.5h-4.5C23 36.5 22 34.5 22 31v-8h-3v-3.8c3.3-.8 5-3.3 5.2-5.2H28.5z" fill="white"/>
      </svg>
    ),
  },
  {
    name: "WhatsApp",
    providerName: "WhatsApp",
    color: "#25D366",
    active: false,
    connectOptions: ["Connect Business"],
    svg: (
      <svg width="52" height="52" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
        <circle cx="26" cy="26" r="26" fill="#25D366"/>
        <path d="M26 13C18.8 13 13 18.8 13 26c0 2.3.6 4.5 1.7 6.4L13 39l6.8-1.7C21.6 38.4 23.8 39 26 39c7.2 0 13-5.8 13-13S33.2 13 26 13zm6.4 17.8c-.3.8-1.6 1.5-2.2 1.6-.6.1-1.2.3-3.8-.8-3.2-1.3-5.2-4.5-5.4-4.7-.2-.2-1.4-1.9-1.4-3.6 0-1.7.9-2.6 1.2-2.9.3-.3.7-.4 1-.4h.7c.3 0 .5.1.8.6.3.5 1 2.5 1.1 2.7.1.2.1.4 0 .6-.1.2-.2.4-.4.6-.2.2-.4.5-.5.6-.2.2-.4.4-.2.8.2.4.9 1.5 2 2.5 1.3 1.1 2.4 1.5 2.8 1.7.4.2.6.1.8-.1.2-.2.9-1 1.1-1.4.2-.4.5-.3.8-.2.3.1 2 .9 2.3 1.1.3.2.6.3.7.4.1.3.1 1.1-.2 1.9z" fill="white"/>
      </svg>
    ),
  },
];

const AddNewAccount = ({
  open,
  onClose,
  onSubmit,
  editingAccount,
  isCloning,
  accountTypes,
  providers,
  initialProvider,
}) => {
  // FIXED: Account Form States with proper field names
  const [accountForm, setAccountForm] = useState({
    type: "",
    provider: "",
    accountName: "",
    connectedBy: "",
    status: true,
    group: null,
    appLink: "", // ADDED: App/Media link field
    // Social Media fields
    username: "",
    accessToken: "",
    pageToken: "", // ADDED: New page token field
    accessTokenExpiry: "", // FIXED: Changed from tokenExpiry to accessTokenExpiry
    pageTokenExpiry: "", // ADDED: New page token expiry field
    // Email fields
    apiKey: "",
    smtpHost: "",
    smtpPort: "",
    smtpUsername: "",
    smtpPassword: "",
    fromEmail: "",
    fromName: "",
    replyToEmail: "",
    // SMS fields
    authToken: "",
    senderId: "",
    country: "",
    region: "",
  });

  // Validation states
  const [accountFormErrors, setAccountFormErrors] = useState({});

  // Groups states - UPDATED: Using new API
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [totalGroups, setTotalGroups] = useState(0);

  // Form submission states
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  // UPDATED: Add Notes field to AddNewAccount component
  const [notes, setNotes] = useState("");

  const watermarkLabel = "OruClick";

  // Platform picker: shown only when adding a brand-new account
  const [showPlatformPicker, setShowPlatformPicker] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // OAuth-based platforms (e.g. Twitter): redirect straight to the provider instead of the manual token form
  const [oauthConnecting, setOauthConnecting] = useState(null); // providerName currently mid-handoff
  const [oauthError, setOauthError] = useState("");

  // UPDATED: Load groups from new dropdown API
  const loadGroups = async () => {
    setLoadingGroups(true);
    try {
      const result = await loadGroupDropdown();

      if (result.success) {
        setGroups(result.data);
        setTotalGroups(result.totalGroups);
        console.log("Successfully loaded group dropdown:", {
          groups: result.data,
          totalGroups: result.totalGroups,
          message: result.message,
        });
      } else {
        console.error("Failed to fetch group dropdown:", result.message);
        setGroups([]);
        setTotalGroups(0);
      }
    } catch (error) {
      console.error("Error loading group dropdown:", error);
      setGroups([]);
      setTotalGroups(0);
    } finally {
      setLoadingGroups(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadGroups(); // Load groups when dialog opens
      // Reset submission states when dialog opens
      setSubmitError("");
      setSubmitSuccess("");
      // Show platform picker only for brand-new accounts with no pre-selected provider
      if (!editingAccount && !isCloning) {
        if (initialProvider) {
          setShowPlatformPicker(false);
          setAccountForm((prev) => ({ ...prev, type: "Social Media", provider: initialProvider }));
          setPdfLoading(true);
        } else {
          setShowPlatformPicker(true);
        }
      } else {
        setShowPlatformPicker(false);
      }
    }
  }, [open, editingAccount, isCloning, initialProvider]);

  // UPDATED: Reset or populate form when dialog opens/closes
  useEffect(() => {
    if (open) {
      if (editingAccount) {
        // When editing, we need to match the selected group from the API data
        const findGroupByName = (groupName) => {
          if (!groupName) return null;

          // Try to find the group by name in the loaded groups
          const foundGroup = groups.find((grp) => grp.groupName === groupName);
          if (foundGroup) {
            return foundGroup;
          }
          // If not found, create a placeholder object with the same structure
          return { groupName: groupName, groupId: null };
        };

        setAccountForm({
          type: editingAccount.type || "",
          provider: editingAccount.provider || "",
          accountName: editingAccount.accountName || "",
          connectedBy: editingAccount.connectedBy || "",
          status: editingAccount.status === "Active",
          group: findGroupByName(editingAccount.group),
          appLink: editingAccount.appLink || "", // ADDED: Set appLink for editing
          // Social Media fields
          username: editingAccount.username || "",
          accessToken: editingAccount.accessToken || "",
          pageToken: editingAccount.pageToken || "", // ADDED: Set pageToken for editing
          accessTokenExpiry:
            editingAccount.tokenExpiry ||
            editingAccount.accessTokenExpiry ||
            "", // UPDATED: Handle both old and new field names
          pageTokenExpiry: editingAccount.pageTokenExpiry || "", // ADDED: Set pageTokenExpiry for editing

          // Email fields
          apiKey: editingAccount.apiKey || "",
          smtpHost: editingAccount.smtpHost || "",
          smtpPort: editingAccount.smtpPort || "",
          smtpUsername: editingAccount.smtpUsername || "",
          smtpPassword: editingAccount.smtpPassword || "",
          fromEmail:
            editingAccount.fromEmail || editingAccount.regionOrFromEmail || "",
          fromName: editingAccount.fromName || "",
          replyToEmail: editingAccount.replyToEmail || "",
          // SMS fields
          authToken: editingAccount.authToken || "",
          senderId: editingAccount.senderId || editingAccount.identifier || "",
          country:
            editingAccount.country || editingAccount.regionOrFromEmail || "",
          region: editingAccount.region || "",
          // REMOVED: notes from accountForm - it should be in separate state
        });

        // ADDED: Set notes in the separate notes state
        setNotes(editingAccount.notes || "");
      } else {
        // Reset form for new account with default Account Type
        resetAccountForm();
      }
      setAccountFormErrors({});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingAccount, groups, accountTypes]); // Added accountTypes dependency

  // Validation function
  const validateAccountForm = () => {
    const errors = {};

    // Common required fields
    if (!accountForm.type.trim()) errors.type = "Type is required";
    if (!accountForm.provider.trim()) errors.provider = "Provider is required";
    if (!accountForm.accountName.trim())
      errors.accountName = "Account name is required";
    if (!accountForm.connectedBy.trim())
      errors.connectedBy = "Connected by is required";

    // Type-specific required fields
    if (accountForm.type === "Social Media") {
      if (!accountForm.username.trim())
        errors.username = "Username/Page ID is required";
      if (!accountForm.accessToken.trim())
        errors.accessToken = "Access token is required";
    }

    if (accountForm.type === "Email") {
      if (!accountForm.fromEmail.trim())
        errors.fromEmail = "From email is required";
      if (!accountForm.fromName.trim())
        errors.fromName = "From name is required";
      // Either API Key or SMTP credentials required
      if (
        !accountForm.apiKey.trim() &&
        (!accountForm.smtpHost.trim() || !accountForm.smtpPort.trim())
      ) {
        errors.apiKey = "API Key or SMTP credentials are required";
        errors.smtpHost = "SMTP Host is required when API Key is not provided";
        errors.smtpPort = "SMTP Port is required when API Key is not provided";
      }
    }

    if (accountForm.type === "SMS") {
      if (!accountForm.authToken.trim())
        errors.authToken = "Auth token is required";
      if (!accountForm.senderId.trim())
        errors.senderId = "Sender ID is required";
      if (!accountForm.country.trim()) errors.country = "Country is required";
    }

    setAccountFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Helper functions
  const getIdentifier = (form) => {
    if (form.type === "Social Media") return form.username;
    if (form.type === "Email") return form.fromEmail;
    if (form.type === "SMS") return form.senderId;
    return "";
  };

  const getRegionOrFromEmail = (form) => {
    if (form.type === "Email") return form.fromEmail;
    if (form.type === "SMS") return form.region || form.country;
    return "";
  };

  // FIXED: Prepare API payload with consistent field names
  const prepareApiPayload = (form) => {
    const basePayload = {
      type: form.type,
      provider: form.provider,
      accountName: form.accountName,
      connectedBy: form.connectedBy,
      status: form.status ? "Active" : "Inactive",
      groupId: form.group?.groupId ? [form.group.groupId] : [], // Send as array, empty array if no group
      groupName: form.group?.groupName || "",
      appLink: form.appLink || "", // ADDED: App/Media link
      notes: notes || "", // ADDED: Include notes in API payload
    };

    // Add type-specific fields based on form data
    if (form.type === "Social Media") {
      return {
        ...basePayload,
        username: form.username,
        accessToken: form.accessToken,
        pageToken: form.pageToken || null, // ADDED: Include pageToken
        accessTokenExpiry: form.accessTokenExpiry || null, // FIXED: Use consistent field name
        pageTokenExpiry: form.pageTokenExpiry || null, // ADDED: Include pageTokenExpiry
      };
    }

    if (form.type === "Email") {
      return {
        ...basePayload,
        apiKey: form.apiKey || null,
        smtpHost: form.smtpHost || null,
        smtpPort: form.smtpPort || null,
        smtpUsername: form.smtpUsername || null,
        smtpPassword: form.smtpPassword || null,
        fromEmail: form.fromEmail,
        fromName: form.fromName,
        replyToEmail: form.replyToEmail || null,
      };
    }

    if (form.type === "SMS") {
      return {
        ...basePayload,
        authToken: form.authToken,
        senderId: form.senderId,
        country: form.country,
        region: form.region || null,
      };
    }

    return basePayload;
  };

  // FIXED: Prepare Edit API payload with consistent field names
  const prepareEditApiPayload = (form) => {
    const basePayload = {
      entityType: "Account",
      accountId: editingAccount.accountId, // Use original accountId
      editMode: "EditAccount",
      type: form.type,
      provider: form.provider,
      accountName: form.accountName,
      connectedBy: form.connectedBy,
      status: form.status ? "Active" : "Inactive",
      groupId: form.group?.groupId || "", // Send as string, empty string if no group
      groupName: form.group?.groupName || "",
      appLink: form.appLink || "", // ADDED: App/Media link
      notes: notes || "", // ADDED: Include notes in API payload
    };

    // Add type-specific fields based on form data
    if (form.type === "Social Media") {
      return {
        ...basePayload,
        username: form.username,
        accessToken: form.accessToken,
        accessTokenExpiry: form.accessTokenExpiry || null, // FIXED: Use consistent field name
        pageTokenExpiry: form.pageTokenExpiry || null, // ADDED: Include pageTokenExpiry
        pageToken: form.pageToken || null, // ADDED: Include pageToken
        tokenExpiry: form.accessTokenExpiry || null, // ADDED: Map to legacy API field if needed
      };
    }

    if (form.type === "Email") {
      return {
        ...basePayload,
        apiKey: form.apiKey || null,
        smtpHost: form.smtpHost || null,
        smtpPort: form.smtpPort || null,
        smtpUsername: form.smtpUsername || null,
        smtpPassword: form.smtpPassword || null,
        fromEmail: form.fromEmail,
        fromName: form.fromName,
        replyToEmail: form.replyToEmail || null,
      };
    }

    if (form.type === "SMS") {
      return {
        ...basePayload,
        authToken: form.authToken,
        senderId: form.senderId,
        country: form.country,
        region: form.region || null,
      };
    }

    return basePayload;
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Clear previous messages
    setSubmitError("");
    setSubmitSuccess("");

    // Validate form
    if (!validateAccountForm()) {
      return;
    }

    setSubmitting(true);

    try {
      // FIXED: Check isCloning flag - if cloning, treat as new account creation
      if (editingAccount && !isCloning) {
        // Only use edit flow if NOT cloning
        // FIXED: Prepare edit API payload with correct field types
        const editPayload = prepareEditApiPayload(accountForm);

        console.log("Submitting account edit with payload:", editPayload);

        // Call edit API
        const result = await editGroupOrAccount(editPayload);

        if (result.success) {
          setSubmitSuccess(result.message || "Account updated successfully");

          // Prepare data for parent component
          const accountData = {
            ...accountForm,
            status: accountForm.status ? "Active" : "Inactive",
            accountId: editingAccount.accountId,
            id: editingAccount.id,
            entityType: editingAccount.entityType || "Account",
            notes: notes,
            // FIXED: Extract only the group name for display
            group: accountForm.group?.groupName || "",
            groupId: accountForm.group?.groupId || "",
            // Add identifier and region fields for display
            identifier: getIdentifier(accountForm),
            regionOrFromEmail: getRegionOrFromEmail(accountForm),
          };

          console.log(
            "Account edit successful, prepared data for parent:",
            accountData
          );

          // Notify parent component
          onSubmit(accountData, "edit");

          // Close dialog after a short delay
          setTimeout(() => {
            handleClose();
          }, 1500);
        } else {
          setSubmitError(result.message || "Failed to update account");
        }
      } else {
        // Create new account OR clone account using POST API
        // FIXED: Prepare create API payload with correct field types
        const createPayload = prepareApiPayload(accountForm);

        console.log(
          isCloning
            ? "Cloning account - creating new account with POST API:"
            : "Submitting new account data:",
          createPayload
        );

        // Use createNewAccount API (POST request)
        const result = await createNewAccount(createPayload);

        if (result.success) {
          setSubmitSuccess(
            isCloning
              ? `Account cloned successfully as "${accountForm.accountName}"`
              : result.message || "Account created successfully"
          );

          // Prepare data for parent component
          const accountData = {
            ...accountForm,
            status: accountForm.status ? "Active" : "Inactive",
            notes: notes,
            id: result.data?.accountId || result.data?.id || Date.now(),
            accountId: result.data?.accountId || result.data?.id,
            // FIXED: Extract only the group name for display
            group: accountForm.group?.groupName || "",
            groupId: accountForm.group?.groupId || "",
            // Add identifier and region fields for display
            identifier: getIdentifier(accountForm),
            regionOrFromEmail: getRegionOrFromEmail(accountForm),
          };

          console.log(
            isCloning
              ? "Account cloned successfully, prepared data for parent:"
              : "Account creation successful, prepared data for parent:",
            accountData
          );

          // Notify parent component
          onSubmit(accountData, "add");

          // Close dialog after a short delay
          setTimeout(() => {
            handleClose();
          }, 1500);
        } else {
          setSubmitError(result.message || "Failed to create account");
        }
      }
    } catch (error) {
      console.error("Error submitting account:", error);
      setSubmitError("An unexpected error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form
  const resetAccountForm = () => {
    setAccountForm({
      type: accountTypes.length > 0 ? accountTypes[0] : "", // UPDATED: Set first account type as default
      provider: "",
      accountName: "",
      connectedBy: "",
      status: true,
      group: null,
      appLink: "", // ADDED: Reset app link
      username: "",
      accessToken: "",
      pageToken: "", // ADDED: Reset pageToken
      accessTokenExpiry: "", // FIXED: Use consistent field name
      pageTokenExpiry: "", // ADDED: New page token expiry field
      apiKey: "",
      smtpHost: "",
      smtpPort: "",
      smtpUsername: "",
      smtpPassword: "",
      fromEmail: "",
      fromName: "",
      replyToEmail: "",
      authToken: "",
      senderId: "",
      country: "",
      region: "",
    });
    setAccountFormErrors({});
    setSubmitError("");
    setSubmitSuccess("");
    setNotes(""); // ADDED: Reset notes in separate state
  };

  // Handle platform selection from picker
  const handlePlatformSelect = (platformName) => {
    setAccountForm((prev) => ({
      ...prev,
      type: "Social Media",
      provider: platformName,
    }));
    setPdfLoading(true);
    setShowPlatformPicker(false);
  };

  // Handle OAuth-based platforms (Twitter): fetch the authUrl from the backend, then redirect
  const handleOAuthConnect = async (platform) => {
    if (oauthConnecting) return;
    setOauthError("");
    setOauthConnecting(platform.providerName);
    try {
      let result;
      if (platform.providerName === "Twitter") {
        result = await initiateTwitterOAuth();
      }

      if (result?.success && result.authUrl) {
        window.location.href = result.authUrl;
      } else {
        setOauthError(result?.message || `Failed to start ${platform.providerName} connection. Please try again.`);
        setOauthConnecting(null);
      }
    } catch (error) {
      setOauthError(`Something went wrong while connecting to ${platform.providerName}. Please try again.`);
      setOauthConnecting(null);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    onClose();
    setAccountFormErrors({});
    setSubmitError("");
    setSubmitSuccess("");
    setNotes(""); // ADDED: Reset notes when closing
    setShowPlatformPicker(false);
  };

  // FIXED: renderConditionalFields with date validation
  const renderConditionalFields = () => {
    if (!accountForm.type) return null;

    return (
      <Box sx={{ mt: 2, mb: 1.5 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 2,
            color: "#1e293b",
            borderBottom: "2px solid #e2e8f0",
            pb: 0.75,
          }}
        >
          {accountForm.type} Account Configuration
        </Typography>

        {accountForm.type === "Social Media" && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Username / Page ID"
                value={accountForm.username}
                onChange={(e) => {
                  setAccountForm({ ...accountForm, username: e.target.value });
                  if (accountFormErrors.username) {
                    setAccountFormErrors({
                      ...accountFormErrors,
                      username: "",
                    });
                  }
                }}
                error={!!accountFormErrors.username}
                helperText={
                  accountFormErrors.username ||
                  "Enter your social media username or page ID"
                }
                disabled={submitting}
                sx={{ mb: 0 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Access Token"
                value={accountForm.accessToken}
                onChange={(e) => {
                  setAccountForm({
                    ...accountForm,
                    accessToken: e.target.value,
                  });
                  if (accountFormErrors.accessToken) {
                    setAccountFormErrors({
                      ...accountFormErrors,
                      accessToken: "",
                    });
                  }
                }}
                error={!!accountFormErrors.accessToken}
                helperText={
                  accountFormErrors.accessToken ||
                  "Paste your access token here"
                }
                disabled={submitting}
                multiline
                minRows={2}
                sx={{
                  mb: 0,
                  "& textarea": { wordBreak: "break-all" },
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Access Token Expiry Date"
                type="date"
                value={accountForm.accessTokenExpiry}
                onChange={(e) =>
                  setAccountForm({
                    ...accountForm,
                    accessTokenExpiry: e.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
                disabled={submitting}
                helperText="Optional: Set access token expiration date"
                sx={{ mb: 0 }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                      🗓️
                    </Box>
                  ),
                }}
                inputProps={{
                  min: getTodayDate(), // ADDED: Prevent selecting past dates
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Page Token (Optional)"
                value={accountForm.pageToken}
                onChange={(e) =>
                  setAccountForm({ ...accountForm, pageToken: e.target.value })
                }
                disabled={submitting}
                multiline
                minRows={2}
                placeholder="Enter page token if different from access token..."
                sx={{
                  mb: 0,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                  "& textarea": { wordBreak: "break-all" },
                }}
                helperText="Optional: Separate page token for specific page access (leave blank if using access token)"
                InputProps={{
                  startAdornment: (
                    <Box
                      sx={{
                        mr: 1,
                        display: "flex",
                        alignItems: "flex-start",
                        pt: 1,
                      }}
                    >
                      🔑
                    </Box>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Page Token Expiry Date (Optional)"
                type="date"
                value={accountForm.pageTokenExpiry}
                onChange={(e) =>
                  setAccountForm({
                    ...accountForm,
                    pageTokenExpiry: e.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
                disabled={submitting}
                helperText="Optional: Set page token expiration date"
                sx={{ mb: 0 }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                      📅
                    </Box>
                  ),
                }}
                inputProps={{
                  min: getTodayDate(), // ADDED: Prevent selecting past dates
                }}
              />
            </Grid>
          </Grid>
        )}

        {accountForm.type === "Email" && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="API Key"
                value={accountForm.apiKey}
                onChange={(e) => {
                  setAccountForm({ ...accountForm, apiKey: e.target.value });
                  if (accountFormErrors.apiKey) {
                    setAccountFormErrors({ ...accountFormErrors, apiKey: "" });
                  }
                }}
                error={!!accountFormErrors.apiKey}
                helperText={
                  accountFormErrors.apiKey ||
                  "Enter API key (required if not using SMTP)"
                }
                disabled={submitting}
                sx={{ mb: 0 }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: "#64748b",
                  textAlign: "center",
                }}
              >
                — OR Use SMTP Configuration —
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SMTP Host"
                value={accountForm.smtpHost}
                onChange={(e) => {
                  setAccountForm({ ...accountForm, smtpHost: e.target.value });
                  if (accountFormErrors.smtpHost) {
                    setAccountFormErrors({
                      ...accountFormErrors,
                      smtpHost: "",
                    });
                  }
                }}
                error={!!accountFormErrors.smtpHost}
                helperText={accountFormErrors.smtpHost}
                disabled={submitting}
                sx={{ mb: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SMTP Port"
                value={accountForm.smtpPort}
                onChange={(e) => {
                  setAccountForm({ ...accountForm, smtpPort: e.target.value });
                  if (accountFormErrors.smtpPort) {
                    setAccountFormErrors({
                      ...accountFormErrors,
                      smtpPort: "",
                    });
                  }
                }}
                error={!!accountFormErrors.smtpPort}
                helperText={accountFormErrors.smtpPort}
                disabled={submitting}
                sx={{ mb: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SMTP Username"
                value={accountForm.smtpUsername}
                onChange={(e) =>
                  setAccountForm({
                    ...accountForm,
                    smtpUsername: e.target.value,
                  })
                }
                disabled={submitting}
                helperText="Optional: SMTP authentication username"
                sx={{ mb: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="SMTP Password"
                type="password"
                value={accountForm.smtpPassword}
                onChange={(e) =>
                  setAccountForm({
                    ...accountForm,
                    smtpPassword: e.target.value,
                  })
                }
                disabled={submitting}
                helperText="Optional: SMTP authentication password"
                sx={{ mb: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Default From Email"
                value={accountForm.fromEmail}
                onChange={(e) => {
                  setAccountForm({ ...accountForm, fromEmail: e.target.value });
                  if (accountFormErrors.fromEmail) {
                    setAccountFormErrors({
                      ...accountFormErrors,
                      fromEmail: "",
                    });
                  }
                }}
                error={!!accountFormErrors.fromEmail}
                helperText={
                  accountFormErrors.fromEmail ||
                  "Email address for outgoing messages"
                }
                disabled={submitting}
                sx={{ mb: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="From Name"
                value={accountForm.fromName}
                onChange={(e) => {
                  setAccountForm({ ...accountForm, fromName: e.target.value });
                  if (accountFormErrors.fromName) {
                    setAccountFormErrors({
                      ...accountFormErrors,
                      fromName: "",
                    });
                  }
                }}
                error={!!accountFormErrors.fromName}
                helperText={
                  accountFormErrors.fromName ||
                  "Display name for outgoing messages"
                }
                disabled={submitting}
                sx={{ mb: 0 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reply-To Email"
                value={accountForm.replyToEmail}
                onChange={(e) =>
                  setAccountForm({
                    ...accountForm,
                    replyToEmail: e.target.value,
                  })
                }
                disabled={submitting}
                helperText="Optional: Different email for replies"
                sx={{ mb: 0 }}
              />
            </Grid>
          </Grid>
        )}

        {accountForm.type === "SMS" && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Auth Token"
                value={accountForm.authToken}
                onChange={(e) => {
                  setAccountForm({ ...accountForm, authToken: e.target.value });
                  if (accountFormErrors.authToken) {
                    setAccountFormErrors({
                      ...accountFormErrors,
                      authToken: "",
                    });
                  }
                }}
                error={!!accountFormErrors.authToken}
                helperText={
                  accountFormErrors.authToken ||
                  "Enter your SMS provider auth token"
                }
                disabled={submitting}
                sx={{ mb: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Sender ID / Phone Number"
                value={accountForm.senderId}
                onChange={(e) => {
                  setAccountForm({ ...accountForm, senderId: e.target.value });
                  if (accountFormErrors.senderId) {
                    setAccountFormErrors({
                      ...accountFormErrors,
                      senderId: "",
                    });
                  }
                }}
                error={!!accountFormErrors.senderId}
                helperText={
                  accountFormErrors.senderId || "Sender ID or phone number"
                }
                disabled={submitting}
                sx={{ mb: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Country"
                value={accountForm.country}
                onChange={(e) => {
                  setAccountForm({ ...accountForm, country: e.target.value });
                  if (accountFormErrors.country) {
                    setAccountFormErrors({ ...accountFormErrors, country: "" });
                  }
                }}
                error={!!accountFormErrors.country}
                helperText={
                  accountFormErrors.country || "Country for SMS service"
                }
                disabled={submitting}
                sx={{ mb: 0 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Region"
                value={accountForm.region}
                onChange={(e) =>
                  setAccountForm({ ...accountForm, region: e.target.value })
                }
                disabled={submitting}
                helperText="Optional: Specific region within country"
                sx={{ mb: 0 }}
              />
            </Grid>
          </Grid>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow:
            "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#4B5AB6",
          color: "white",
          fontWeight: 600,
          fontSize: "1.25rem",
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {/* Back arrow: shown only when we moved from picker to form for a new account */}
        {!showPlatformPicker && !editingAccount && !isCloning && (
          <Tooltip title="Back to platform selection">
            <IconButton
              onClick={() => {
                setShowPlatformPicker(true);
                setAccountForm((prev) => ({ ...prev, type: "Social Media", provider: "" }));
              }}
              size="small"
              sx={{ color: "white", mr: 0.5, "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {showPlatformPicker
          ? "Connect an Account"
          : isCloning
          ? "Clone Account"
          : editingAccount
          ? "Edit Account"
          : "Add New Account"}
      </DialogTitle>

      <DialogContent sx={{ p: 0, pb: 0, overflow: (editingAccount && !isCloning) ? "auto" : "hidden", display: "flex", flexDirection: "column" }}>
        {/* ── PLATFORM PICKER ─────────────────────────────────────── */}
        {showPlatformPicker ? (
          <Box sx={{ p: 3 }}>
            <Typography
              variant="body2"
              sx={{ color: "#64748b", mb: 3, textAlign: "center", fontSize: "0.9rem" }}
            >
              Choose the social network you want to connect.
            </Typography>

            {oauthError && (
              <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setOauthError("")}>
                {oauthError}
              </Alert>
            )}

            {/* ── Active platforms ── */}
            <Grid container spacing={2.5}>
              {PLATFORMS.filter((p) => p.active).map((platform) => {
                const isConnecting = oauthConnecting === platform.providerName;
                const isBlocked = !!oauthConnecting && !isConnecting;

                return (
                <Grid item xs={12} sm={3} key={platform.name}>
                  <Box
                    onClick={
                      isBlocked
                        ? undefined
                        : platform.oauth
                        ? () => handleOAuthConnect(platform)
                        : () => handlePlatformSelect(platform.providerName)
                    }
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      border: "1.5px solid #e2e8f0",
                      bgcolor: "#ffffff",
                      cursor: isBlocked ? "default" : "pointer",
                      opacity: isBlocked ? 0.5 : 1,
                      transition: "all 0.2s ease",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      position: "relative",
                      "&:hover": isBlocked ? {} : {
                        borderColor: platform.color,
                        boxShadow: `0 8px 24px ${platform.color}28`,
                        transform: "translateY(-3px)",
                        "& .platform-top-bar": { opacity: 1 },
                        "& .platform-arrow": { opacity: 1, transform: "translateX(0)" },
                      },
                    }}
                  >
                    {isConnecting && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          zIndex: 2,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 1,
                          bgcolor: "rgba(255,255,255,0.9)",
                        }}
                      >
                        <CircularProgress size={28} sx={{ color: platform.color }} />
                        <Typography fontSize="0.75rem" fontWeight={700} color={platform.color}>
                          Connecting…
                        </Typography>
                      </Box>
                    )}
                    {/* Coloured top accent bar */}
                    <Box
                      className="platform-top-bar"
                      sx={{
                        width: "100%",
                        height: 4,
                        background: `linear-gradient(90deg, ${platform.color}cc, ${platform.color})`,
                        opacity: 0.35,
                        transition: "opacity 0.2s ease",
                      }}
                    />

                    <Box sx={{ pt: 2.5, pb: 2, px: 2, width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
                      {/* Icon */}
                      <Box sx={{ mb: 1.5 }}>{platform.svg}</Box>

                      {/* Name */}
                      <Typography
                        sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1e293b", mb: 1.5 }}
                      >
                        {platform.name}
                      </Typography>

                      {/* Connect option pills */}
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, width: "100%" }}>
                        {platform.connectOptions.map((opt) => (
                          <Box
                            key={opt}
                            sx={{
                              bgcolor: `${platform.color}12`,
                              border: `1px solid ${platform.color}30`,
                              borderRadius: 6,
                              py: 0.5,
                              px: 1.5,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 0.5,
                            }}
                          >
                            <Box
                              sx={{
                                width: 5,
                                height: 5,
                                borderRadius: "50%",
                                bgcolor: platform.color,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{ color: platform.color, fontSize: "0.7rem", fontWeight: 600 }}
                            >
                              {opt}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* Arrow hint */}
                    <Typography
                      className="platform-arrow"
                      sx={{
                        position: "absolute",
                        bottom: 10,
                        right: 12,
                        fontSize: "0.7rem",
                        color: platform.color,
                        fontWeight: 700,
                        opacity: 0,
                        transform: "translateX(-4px)",
                        transition: "all 0.2s ease",
                      }}
                    >
                      Connect →
                    </Typography>
                  </Box>
                </Grid>
                );
              })}
            </Grid>

            {/* Coming Soon divider */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 3 }}>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "#e2e8f0" }} />
              <Typography
                variant="caption"
                sx={{
                  color: "#94a3b8",
                  fontWeight: 700,
                  fontSize: "0.68rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  whiteSpace: "nowrap",
                  px: 1,
                }}
              >
                🔒 Coming Soon
              </Typography>
              <Box sx={{ flex: 1, height: "1px", bgcolor: "#e2e8f0" }} />
            </Box>

            {/* ── Coming Soon platforms ── */}
            <Grid container spacing={1.5}>
              {PLATFORMS.filter((p) => !p.active).map((platform) => (
                <Grid item xs={6} sm={3} key={platform.name}>
                  <Box
                    sx={{
                      borderRadius: 2.5,
                      border: "1.5px dashed #e2e8f0",
                      bgcolor: "#fafafa",
                      py: 2,
                      px: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                      opacity: 0.65,
                    }}
                  >
                    <Box sx={{ filter: "grayscale(70%) opacity(0.7)" }}>{platform.svg}</Box>
                    <Typography
                      sx={{ fontWeight: 600, fontSize: "0.8rem", color: "#94a3b8" }}
                    >
                      {platform.name}
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: "#f1f5f9",
                        borderRadius: 4,
                        px: 1,
                        py: 0.25,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.6rem", color: "#94a3b8", fontWeight: 700, letterSpacing: "0.05em" }}
                      >
                        COMING SOON
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <Box sx={{ display: "flex", minHeight: 0, ...(!(editingAccount && !isCloning) && { height: "88vh" }), flex: 1 }}>
            <Box sx={{
              ...(editingAccount && !isCloning
                ? { flex: 1 }
                : { flex: 1, borderRight: "1px solid #e2e8f0", overflowY: "auto" }
              ),
              p: 2.5, pb: 2
            }}>
          <Box>
        {/* Display success/error messages */}
        {submitError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {submitError}
          </Alert>
        )}

        {submitSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {submitSuccess}
          </Alert>
        )}

        {/* Basic Account Information */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 2,
            color: "#1e293b",
            borderBottom: "2px solid #e2e8f0",
            pb: 0.75,
          }}
        >
          Basic Account Information
        </Typography>

        <Grid container spacing={2} sx={{ mb: 1.5 }}>
          {/* Type Selection */}
          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              required
              error={!!accountFormErrors.type}
              disabled={submitting}
            >
              <InputLabel>Account Type</InputLabel>
              <Select
                value={accountForm.type}
                label="Account Type"
                onChange={(e) => {
                  setAccountForm({
                    ...accountForm,
                    type: e.target.value,
                    provider: "", // Reset provider when type changes
                  });
                  if (accountFormErrors.type) {
                    setAccountFormErrors({ ...accountFormErrors, type: "" });
                  }
                }}
              >
                {accountTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              {accountFormErrors.type && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 2 }}
                >
                  {accountFormErrors.type}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Provider Selection */}
          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              required
              error={!!accountFormErrors.provider}
              disabled={submitting || !accountForm.type}
            >
              <InputLabel>Provider</InputLabel>
              <Select
                value={accountForm.provider}
                label="Provider"
                onChange={(e) => {
                  setAccountForm({ ...accountForm, provider: e.target.value });
                  setPdfLoading(true);
                  if (accountFormErrors.provider) {
                    setAccountFormErrors({
                      ...accountFormErrors,
                      provider: "",
                    });
                  }
                }}
              >
                {providers[accountForm.type]?.map((provider) => (
                  <MenuItem key={provider} value={provider}>
                    {provider}
                  </MenuItem>
                )) || []}
              </Select>
              {accountFormErrors.provider && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, ml: 2 }}
                >
                  {accountFormErrors.provider}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Account Name */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Account Name"
              value={accountForm.accountName}
              onChange={(e) => {
                setAccountForm({ ...accountForm, accountName: e.target.value });
                if (accountFormErrors.accountName) {
                  setAccountFormErrors({
                    ...accountFormErrors,
                    accountName: "",
                  });
                }
              }}
              error={!!accountFormErrors.accountName}
              helperText={
                accountFormErrors.accountName ||
                "Enter a descriptive name for this account"
              }
              disabled={submitting}
            />
          </Grid>

          {/* Connected By */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Connected By"
              value={accountForm.connectedBy}
              onChange={(e) => {
                setAccountForm({ ...accountForm, connectedBy: e.target.value });
                if (accountFormErrors.connectedBy) {
                  setAccountFormErrors({
                    ...accountFormErrors,
                    connectedBy: "",
                  });
                }
              }}
              error={!!accountFormErrors.connectedBy}
              helperText={
                accountFormErrors.connectedBy || "Who set up this account"
              }
              disabled={submitting}
            />
          </Grid>

          {/* Group Selection */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              fullWidth
              options={groups}
              getOptionLabel={(option) => option?.groupName || ""}
              isOptionEqualToValue={(option, value) =>
                option?.groupId === value?.groupId
              }
              value={accountForm.group}
              onChange={(event, newValue) => {
                setAccountForm({ ...accountForm, group: newValue });
              }}
              loading={loadingGroups}
              disabled={submitting || loadingGroups}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Group (Optional)"
                  helperText={`Optional: Select a group (${totalGroups} available)`}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingGroups ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {option.groupName}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      ID: {option.groupId}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Grid>

                    {/* App/Media Link */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="App/Media Link"
              value={accountForm.appLink}
              onChange={(e) => {
                setAccountForm({
                  ...accountForm,
                  appLink: e.target.value,
                });
              }}
              placeholder="https://facebook.com/yourpage or https://instagram.com/yourprofile"
              helperText="Optional: Add a direct link to your social media page, app, or service dashboard"
              disabled={submitting}
              InputProps={{
                startAdornment: (
                  <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                    🔗
                  </Box>
                ),
              }}
            />
          </Grid>


          {/* Status */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={accountForm.status}
                  onChange={(e) =>
                    setAccountForm({ ...accountForm, status: e.target.checked })
                  }
                  disabled={submitting}
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">Account Status:</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: accountForm.status ? "#059669" : "#dc2626",
                    }}
                  >
                    {accountForm.status ? "Active" : "Inactive"}
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>

        {/* Type-specific Configuration */}
        {renderConditionalFields()}

        {/* MOVED: Notes Field - Always show notes field regardless of account type */}
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1.5,
              color: "#1e293b",
              borderBottom: "2px solid #e2e8f0",
              pb: 0.75,
            }}
          >
            Additional Information
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                placeholder="Add any additional notes or information..."
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={submitting}
                InputProps={{
                  startAdornment: (
                    <Box
                      sx={{
                        mr: 1,
                        display: "flex",
                        alignItems: "flex-start",
                        pt: 1,
                      }}
                    >
                      📝
                    </Box>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
                helperText="Optional: Add notes, configuration details, or any other relevant information"
              />
            </Grid>
          </Grid>
        </Box>
          </Box>
            </Box>

            {!(editingAccount && !isCloning) && <Box
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: "50%",
                display: { xs: "none", md: "flex" },
                flexDirection: "column",
                borderLeft: "1px solid #e2e8f0",
              }}
            >
              {/* Header bar */}
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  borderBottom: "1px solid #e2e8f0",
                  bgcolor: "#f8fafc",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 800, color: "#4B5AB6", letterSpacing: 1, fontSize: "0.7rem", flex: 1 }}
                >
                  📋 Setup Guide
                </Typography>
                {accountForm.provider && PROVIDER_GUIDES[accountForm.provider]?.title && (
                  <Typography
                    fontSize="0.72rem"
                    fontWeight={700}
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: `${PROVIDER_GUIDES[accountForm.provider].color}15`,
                      color: PROVIDER_GUIDES[accountForm.provider].color,
                    }}
                  >
                    {PROVIDER_GUIDES[accountForm.provider].title}
                  </Typography>
                )}
              </Box>

              {/* PDF iframe — fills remaining height */}
              {accountForm.provider && PROVIDER_GUIDES[accountForm.provider]?.pdfUrl ? (
                <Box sx={{ flex: 1, position: "relative", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  {pdfLoading && (
                    <Box sx={{ position: "absolute", inset: 0, zIndex: 3, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.85)", gap: 1.5 }}>
                      <CircularProgress size={36} sx={{ color: PROVIDER_GUIDES[accountForm.provider]?.color || "#4B5AB6" }} />
                      <Typography fontSize="0.78rem" fontWeight={600} color="#64748b">Loading guide…</Typography>
                    </Box>
                  )}
                  <Box
                    component="iframe"
                    key={accountForm.provider}
                    src={`${PROVIDER_GUIDES[accountForm.provider].pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                    title={`${accountForm.provider} Setup Guide`}
                    onLoad={() => setPdfLoading(false)}
                    sx={{
                      position: "absolute",
                      top: "-20px",
                      left: 0,
                      right: 0,
                      height: "calc(100% + 32px)",
                      border: "none",
                      display: "block",
                      width: "100%",
                    }}
                  />
                  {/* Watermark overlay */}
                  <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, pointerEvents: "none", overflow: "hidden", zIndex: 1, display: "grid", gridTemplateColumns: "repeat(2, 1fr)" }}>
                    {Array.from({ length: 30 }).map((_, i) => (
                      <Box key={i} sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
                        <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "rgba(75,90,182,0.13)", transform: "rotate(-35deg)", userSelect: "none", whiteSpace: "nowrap", letterSpacing: "0.04em" }}>
                          {watermarkLabel}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    p: 3,
                    bgcolor: "#f8fafc",
                  }}
                >
                  <Typography fontSize="2rem" lineHeight={1}>📄</Typography>
                  <Typography fontSize="0.82rem" fontWeight={600} color="#64748b" textAlign="center">
                    {accountForm.provider
                      ? `No guide available for ${accountForm.provider} yet.`
                      : "Select a Provider to see its setup guide here."}
                  </Typography>
                </Box>
              )}
            </Box>}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          justifyContent: showPlatformPicker ? "flex-end" : "space-between",
          bgcolor: "#f8fafc",
        }}
      >
        {/* Platform picker mode: only a cancel button */}
        {showPlatformPicker ? (
          <Button
            onClick={handleClose}
            sx={{ color: "#64748b" }}
          >
            Cancel
          </Button>
        ) : (
          <>
            <Button
              onClick={resetAccountForm}
              variant="outlined"
              disabled={submitting}
              sx={{
                color: "#64748b",
                borderColor: "#e2e8f0",
                "&:hover": {
                  borderColor: "#cbd5e1",
                  bgcolor: "#f1f5f9",
                },
              }}
            >
              Reset Form
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                onClick={handleClose}
                disabled={submitting}
                sx={{ color: "#64748b" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                type="submit"
                variant="contained"
                disabled={loadingGroups || submitting}
                sx={{
                  bgcolor: "#4B5AB6",
                  "&:hover": { bgcolor: "#3a4891" },
                  borderRadius: 2,
                  px: 4,
                }}
              >
                {submitting ? (
                  <CircularProgress size={24} />
                ) : isCloning ? (
                  "Clone Account"
                ) : editingAccount ? (
                  "Update Account"
                ) : (
                  "Add Account"
                )}
              </Button>
            </Box>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddNewAccount;
